import { useCallback, useState } from 'react';

import type { AcceptIngest } from '@dcc-erp/shared';

import { HttpError } from '../../lib/api';
import { useAcceptIngest, useRejectIngest } from '../../lib/queries/doc-ingest';

export interface UseAcceptRejectArgs {
  companyId: string | undefined;
  ingestId: string;
  hasCritical: boolean;
  vendorId: string | null;
}

export interface AcceptResult {
  apBillId: string;
  status: 'POSTED' | 'PENDING_APPROVAL';
  anomalyReasons: string[];
}

export function useAcceptReject({
  companyId,
  ingestId,
  hasCritical,
  vendorId,
}: UseAcceptRejectArgs) {
  const acceptMut = useAcceptIngest(companyId, ingestId);
  const rejectMut = useRejectIngest(companyId, ingestId);
  const [result, setResult] = useState<AcceptResult | null>(null);
  const [conflict, setConflict] = useState<HttpError | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Hard Accept gate (spec §8): critical validation OR missing vendor blocks,
  // each with a human Thai reason (never a dead grey button).
  const canAccept = !hasCritical && !!vendorId;
  const gateReason = hasCritical
    ? 'แก้ไขข้อผิดพลาดร้ายแรงก่อนบันทึก'
    : !vendorId
      ? 'เลือกผู้ขายก่อนบันทึก'
      : null;

  const accept = useCallback(
    async (payload: AcceptIngest) => {
      setError(null);
      setConflict(null);
      try {
        const r = await acceptMut.mutateAsync(payload);
        setResult(r);
        return r;
      } catch (e) {
        // 409 = backend dedup (P2002). Keep the form + edits; surface in place.
        if (e instanceof HttpError && e.status === 409) {
          setConflict(e);
        } else if (e instanceof HttpError) {
          setError(e.body.detail ?? e.message);
        } else {
          setError('เกิดข้อผิดพลาด');
        }
        throw e;
      }
    },
    [acceptMut],
  );

  const reject = useCallback(
    async (reason: string) => {
      // Conditionally spread so we never assign `reason: undefined` —
      // exactOptionalPropertyTypes forbids that on the optional mutation arg.
      await rejectMut.mutateAsync(reason ? { reason } : {});
    },
    [rejectMut],
  );

  return {
    canAccept,
    gateReason,
    accept,
    reject,
    result,
    conflict,
    error,
    busy: acceptMut.isPending || rejectMut.isPending,
  };
}
